/**
 * Intentional, optional sound + haptic cues (SFX only).
 * Background music lives in src/audio/adventureMusicEngine.js.
 *
 * All audio is original Web Audio oscillator tones synthesized at runtime —
 * no samples, libraries, or third-party sound assets.
 */

const SOUND_KEY = 'wesam-feedback-sound';
const HAPTIC_KEY = 'wesam-feedback-haptic';
const VOLUME = 2;

let audioCtx = null;

function readPref(key, fallback) {
  if (fallback === undefined) fallback = true;
  try {
    var raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === 'true';
  } catch (err) {
    return fallback;
  }
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function soundAllowed() {
  return readPref(SOUND_KEY) && !prefersReducedMotion();
}

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  var Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioCtx) audioCtx = new Ctx();
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(function () {});
  }
  return audioCtx;
}

function tone(opts) {
  var frequency = opts.frequency;
  var duration = opts.duration != null ? opts.duration : 0.08;
  var type = opts.type || 'sine';
  var gain = opts.gain != null ? opts.gain : 0.06;
  var attack = opts.attack != null ? opts.attack : 0.008;
  var detune = opts.detune || 0;

  if (!soundAllowed()) return;
  var ctx = getAudioContext();
  if (!ctx) return;

  var level = Math.min(gain * VOLUME, 0.25);
  var now = ctx.currentTime;
  var osc = ctx.createOscillator();
  var amp = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(frequency, now);
  if (detune) osc.detune.setValueAtTime(detune, now);
  amp.gain.setValueAtTime(0.0001, now);
  amp.gain.exponentialRampToValueAtTime(level, now + attack);
  amp.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(amp);
  amp.connect(ctx.destination);
  osc.start(now);
  osc.stop(now + duration + 0.02);
}

function vibrate(pattern) {
  if (!readPref(HAPTIC_KEY) || prefersReducedMotion()) return;
  if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') {
    return;
  }
  try {
    navigator.vibrate(pattern);
  } catch (err) {
    // unsupported / blocked
  }
}

/** Soft collect click — reward moment, not a slot machine. */
export function confirmCollect() {
  tone({ frequency: 880, duration: 0.07, type: 'triangle', gain: 0.045 });
  tone({
    frequency: 1320,
    duration: 0.09,
    type: 'sine',
    gain: 0.03,
    attack: 0.012,
  });
  vibrate(12);
}

/** Whoops — snake bite miss-cue (original oscillator tones). */
export function confirmWhoops() {
  tone({ frequency: 220, duration: 0.11, type: 'sawtooth', gain: 0.04 });
  tone({
    frequency: 165,
    duration: 0.16,
    type: 'triangle',
    gain: 0.035,
    attack: 0.01,
  });
  setTimeout(function () {
    tone({ frequency: 110, duration: 0.14, type: 'sine', gain: 0.03 });
  }, 90);
  vibrate([30, 40, 50]);
}

/** Game over / loss — descending original tones. */
export function confirmLoss() {
  var notes = [
    { f: 392, t: 0, d: 0.14 },
    { f: 311.13, t: 120, d: 0.16 },
    { f: 246.94, t: 260, d: 0.2 },
    { f: 185, t: 420, d: 0.32 },
  ];
  notes.forEach(function (n) {
    setTimeout(function () {
      tone({
        frequency: n.f,
        duration: n.d,
        type: n.t >= 260 ? 'sawtooth' : 'triangle',
        gain: n.t >= 260 ? 0.05 : 0.04,
        attack: 0.012,
      });
    }, n.t);
  });
  vibrate([40, 50, 40, 50, 80]);
}

/** Gentle success when advancing (e.g. home → about). */
export function confirmAdvance() {
  tone({ frequency: 523.25, duration: 0.1, type: 'sine', gain: 0.05 });
  setTimeout(function () {
    tone({ frequency: 659.25, duration: 0.12, type: 'sine', gain: 0.045 });
  }, 70);
  setTimeout(function () {
    tone({ frequency: 783.99, duration: 0.16, type: 'triangle', gain: 0.035 });
  }, 140);
  vibrate([18, 40, 28]);
}

/** Achievement fanfare — bird reaches Contact. */
export function celebrateWin() {
  var notes = [
    { f: 523.25, t: 0 },
    { f: 659.25, t: 90 },
    { f: 783.99, t: 180 },
    { f: 1046.5, t: 280 },
    { f: 783.99, t: 420 },
    { f: 1046.5, t: 520 },
  ];
  notes.forEach(function (n) {
    setTimeout(function () {
      tone({
        frequency: n.f,
        duration: n.t === 520 ? 0.28 : 0.12,
        type: n.t >= 280 ? 'triangle' : 'sine',
        gain: n.t >= 280 ? 0.055 : 0.045,
      });
    }, n.t);
  });
  vibrate([20, 40, 20, 40, 45]);
}

export function motionOk() {
  return !prefersReducedMotion();
}

export function setSoundEnabled(enabled) {
  try {
    localStorage.setItem(SOUND_KEY, String(!!enabled));
  } catch (err) {
    // ignore
  }
}

export function setHapticEnabled(enabled) {
  try {
    localStorage.setItem(HAPTIC_KEY, String(!!enabled));
  } catch (err) {
    // ignore
  }
}

export function isSoundEnabled() {
  return readPref(SOUND_KEY);
}

export function isHapticEnabled() {
  return readPref(HAPTIC_KEY);
}
